import React from "react";
import { block, asset, C, CONTENT, Icon, Eyebrow, Tag, SmartVideo, Screenshot, RegisterButton, Wordmark, LangSwitch, MultilineText, OsIcon, anchorHref, pathHref } from "./shared";

/* ── vertical format + phone video ────────────────────────────── */
export function Formats() {
  const b = block("formats");
  if (!b) return null;
  return (
    <section id="formats" style={{ margin: "72px clamp(14px,3vw,28px)", borderRadius: "var(--radius-xl)", background: "var(--bg-sunken)", border: "1px solid var(--border-soft)", overflow: "hidden", scrollMarginTop: 76 }} data-screen-label="Formats">
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "clamp(24px,4vw,56px)", alignItems: "center", padding: "clamp(36px,5vw,60px)" }} className="fr-vert">
        <div className="reveal">
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <h2 style={{ fontSize: "clamp(28px,3.4vw,42px)", margin: "12px 0 16px", letterSpacing: "-0.02em", lineHeight: 1.08, maxWidth: 500, textWrap: "balance" }}>
            <MultilineText text={b.title} />
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-body)", margin: "0 0 22px", maxWidth: 470 }}>
            {b.body}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 26 }}>
            <Tag mono>9:16</Tag><Tag mono>16:9</Tag><Tag mono>1:1</Tag>
          </div>
          <div style={{ display: "grid", gap: 14, maxWidth: 520 }}>
            {(b.points || []).map((p, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Icon name={p.icon || "check"} size={19} color="var(--accent)" />
                <span style={{ fontSize: 15, color: "var(--text-body)", fontWeight: 500, lineHeight: 1.45 }}>{p.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="reveal" style={{ "--i": 1, justifySelf: "center" }}>
          <div className="fr-bob" style={{ "--d": "7.5s" }}>
            <PhoneFrame />
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneFrame() {
  return (
    <div style={{ justifySelf: "center", position: "relative", width: 268, padding: 10, borderRadius: 42, background: "linear-gradient(160deg, var(--clay-4) 0%, var(--clay-6) 100%)", boxShadow: "var(--shadow-lg), var(--shadow-clay-inset)", flex: "none" }}>
      <div style={{ position: "relative", borderRadius: 33, overflow: "hidden", background: "var(--clay-9)", aspectRatio: "9 / 16" }}>
        <SmartVideo src={asset("videos/S1E2_assembly.mp4")} poster={asset("video-poster.png")} watchId="formats" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 86, height: 22, background: "var(--clay-9)", borderRadius: "0 0 14px 14px" }}></div>
        <span style={{ position: "absolute", left: 12, bottom: 12, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--clay-0)", background: "rgba(44,43,38,0.55)", padding: "3px 8px", borderRadius: "var(--radius-xs)", backdropFilter: "blur(4px)" }}>S1 · E2 — assembly</span>
      </div>
      {/* a 16:9 cut peeking out — the same story, widescreen */}
      <div style={{ position: "absolute", left: -44, bottom: -26, width: 168, padding: 7, borderRadius: 16, background: "linear-gradient(160deg, var(--clay-3) 0%, var(--clay-5) 100%)", boxShadow: "var(--shadow-lg), var(--shadow-clay-inset)", zIndex: 2 }}>
        <div style={{ position: "relative", borderRadius: 11, overflow: "hidden", aspectRatio: "16 / 9", background: "var(--clay-9)" }}>
          <img src={asset("frames/loc-signal-street.jpg")} alt="16:9 widescreen cut" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <span style={{ position: "absolute", left: 7, bottom: 7, fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--clay-0)", background: "rgba(44,43,38,0.55)", padding: "2px 6px", borderRadius: "var(--radius-xs)", backdropFilter: "blur(4px)" }}>16:9</span>
        </div>
      </div>
    </div>
  );
}

/* ── cta: bare finale — no panel, just the studio screenshot and a
   registration link on the page background, air around the dark
   Content Memory above and the ink footer below ── */
export function CTA() {
  const b = block("cta");
  if (!b) return null;
  return (
    <section id="cta" style={{ maxWidth: 1220, margin: "0 auto", padding: "clamp(72px,9vw,120px) clamp(20px,5vw,56px) clamp(40px,5vw,64px)", scrollMarginTop: 76 }} data-screen-label="CTA">
      <div className="fr-cta-grid" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: "clamp(28px,4.5vw,72px)", alignItems: "center" }}>
        <div className="reveal">
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <h2 style={{ fontSize: "clamp(30px,3.8vw,50px)", margin: "14px 0 16px", letterSpacing: "-0.025em", lineHeight: 1.05, maxWidth: 520, textWrap: "balance", color: "var(--text-display)" }}>
            <MultilineText text={b.title} />
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-muted)", margin: "0 0 28px", maxWidth: 460 }}>
            {b.body}
          </p>
          <RegisterButton label={b.cta_label} url={b.cta_url} />
          {b.cta_note && <p style={{ fontSize: 13, color: "var(--text-faint)", margin: "14px 0 0" }}>{b.cta_note}</p>}
        </div>
        <div className="reveal fr-showcase-media" style={{ "--i": 1, minWidth: 0 }}>
          <Screenshot media={b.media} alt={b.title} />
        </div>
      </div>
    </section>
  );
}

/* ── desktop app download: app-store style, Mac now, Windows soon ── */
function StoreButton({ icon, top, main, url, muted }) {
  const style = {
    display: "inline-flex", alignItems: "center", gap: 14, textDecoration: "none",
    padding: "14px 26px 15px 22px", borderRadius: "var(--radius-md)", minWidth: 250,
    background: muted ? "transparent" : "var(--store-bg)",
    border: muted ? "1.5px dashed var(--border-strong)" : "1.5px solid var(--store-bg)",
    boxShadow: muted ? "none" : "var(--shadow-md), var(--shadow-clay-inset)",
    cursor: url ? "pointer" : "default",
  };
  const body = (
    <React.Fragment>
      <OsIcon name={icon} size={30} color={muted ? "var(--text-faint)" : "var(--store-fg)"} />
      <span style={{ display: "grid", gap: 1, textAlign: "left" }}>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: muted ? "var(--text-faint)" : "var(--store-sub)" }}>{top}</span>
        <span style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 700, lineHeight: 1.15, color: muted ? "var(--text-muted)" : "var(--store-fg)", whiteSpace: "nowrap" }}>{main}</span>
      </span>
    </React.Fragment>
  );
  return url
    ? <a href={url} className="fr-lift" style={style}>{body}</a>
    : <span className={muted ? undefined : "fr-lift"} style={style}>{body}</span>;
}

const releaseTop = (release) => `${C.download} · ${release.version}`;

export function DownloadApp() {
  const b = block("download");
  if (!b) return null;
  const { mac, win } = CONTENT.releases || {};
  return (
    <section id="download" style={{ maxWidth: 1080, margin: "0 auto", padding: "clamp(56px,7vw,96px) clamp(20px,5vw,56px) clamp(64px,8vw,104px)", scrollMarginTop: 76 }} data-screen-label="Download">
      <div className="reveal fr-dl-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: "clamp(28px,5vw,72px)", borderTop: "1px solid var(--border-soft)", paddingTop: "clamp(40px,5vw,64px)" }}>
        <div>
          <h2 style={{ margin: "0 0 10px", fontSize: "clamp(26px,3.2vw,38px)", letterSpacing: "-0.025em", lineHeight: 1.08, color: "var(--text-display)", textWrap: "balance", maxWidth: 520 }}>{b.title}</h2>
          <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55, color: "var(--text-muted)", maxWidth: 440 }}>{b.body}</p>
        </div>
        <div className="fr-dl-buttons" style={{ display: "grid", gap: 12, justifyItems: "stretch" }}>
          {/* a button lights up once a release is featured in Admin → Releases
              (or, for Mac, once mac_url is set in the block) */}
          <StoreButton icon="apple" top={mac ? releaseTop(mac) : b.mac_top} main={b.mac_main} url={mac?.url || b.mac_url} muted={!(mac?.url || b.mac_url)} />
          <StoreButton icon="windows" top={win ? releaseTop(win) : b.win_top} main={b.win_main} url={win?.url} muted={!win} />
        </div>
      </div>
    </section>
  );
}

/* ── MCP integrations: the studio inside Claude and ChatGPT ────── */
export function Mcp() {
  const b = block("mcp");
  if (!b) return null;
  const chip = {
    display: "inline-flex", alignItems: "center", gap: 14,
    padding: "14px 26px 15px 22px", borderRadius: "var(--radius-md)", minWidth: 230,
  };
  const clients = [["claude", "Claude"], ["openai", "ChatGPT"]];
  return (
    <section id="mcp" style={{ maxWidth: 1080, margin: "0 auto", padding: "clamp(56px,7vw,96px) clamp(20px,5vw,56px) 0", scrollMarginTop: 76 }} data-screen-label="MCP">
      <div className="reveal fr-dl-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: "clamp(28px,5vw,72px)" }}>
        <div>
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <h2 style={{ margin: "12px 0 10px", fontSize: "clamp(26px,3.2vw,38px)", letterSpacing: "-0.025em", lineHeight: 1.08, color: "var(--text-display)", textWrap: "balance", maxWidth: 520 }}>
            <MultilineText text={b.title} />
          </h2>
          <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55, color: "var(--text-muted)", maxWidth: 460 }}>{b.body}</p>
        </div>
        <div className="fr-dl-buttons" style={{ display: "grid", gap: 12, justifyItems: "stretch" }}>
          {clients.map(([icon, name]) => (
            <span key={icon} className="fr-lift" style={chip}>
              <OsIcon name={icon} size={28} color="var(--text-display)" />
              <span style={{ display: "grid", gap: 1, textAlign: "left" }}>
                <span style={{ fontFamily: "var(--font-body)", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-faint)" }}>MCP</span>
                <span style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 700, lineHeight: 1.15, color: "var(--text-display)", whiteSpace: "nowrap" }}>{name}</span>
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── contact line: a light one-row sign-off before the dark footer ── */
export function Contact() {
  const b = block("contact");
  if (!b) return null;
  const company = window.__FRAMERIVER_COMPANY__ || DEFAULT_COMPANY;
  const email = b.email || company.contact_email;
  return (
    <section id="contact" style={{ maxWidth: 760, margin: "0 auto", padding: "clamp(40px,5vw,64px) clamp(20px,5vw,56px) clamp(56px,7vw,88px)", textAlign: "center", scrollMarginTop: 76 }} data-screen-label="Contact">
      <div className="reveal" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 17, fontWeight: 600, fontFamily: "var(--font-display)", color: "var(--text-display)" }}>{b.title}</p>
        <p style={{ margin: 0, fontSize: 14.5, color: "var(--text-muted)" }}>
          {b.note}{" "}
          <a href={`mailto:${email}`} style={{ color: "var(--accent-text)", fontWeight: 600, textDecorationColor: "var(--accent-tint)" }}>{email}</a>
        </p>
      </div>
    </section>
  );
}

/* ── footer ───────────────────────────────────────────────────── */
const DEFAULT_COMPANY = {
  legal_name: "SELF MEDIA LLC",
  contact_email: "aapanasenkov@gmail.com",
  footer_copyright: `© ${new Date().getFullYear()} Frameriver · Operated by SELF MEDIA LLC`,
  privacy_path: "/privacy",
  personal_data_path: "/personal-data",
  cookies_path: "/cookies",
  terms_path: "/terms",
};

export function Footer() {
  const company = window.__FRAMERIVER_COMPANY__ || DEFAULT_COMPANY;
  const F = C.footer;
  const footerStyle = { fontSize: 12.5, color: "var(--footer-faint)" };
  const linkStyle = { color: "var(--footer-link)", textDecoration: "none" };

  return (
    <footer style={{ background: "var(--footer-bg)", borderTop: "1px solid var(--footer-border)", color: "var(--footer-text)", padding: "clamp(40px,5vw,56px) clamp(20px,5vw,56px) 32px" }}>
      <div style={{ display: "flex", gap: 60, maxWidth: 1180, margin: "0 auto", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 220 }}>
          <Wordmark light />
          <p style={{ fontSize: 13.5, maxWidth: 300, lineHeight: 1.6, margin: "12px 0 0", color: "var(--footer-text)" }}>{F.tagline}</p>
          <div style={{ marginTop: 18 }}>
            <LangSwitch light />
          </div>
        </div>
        {F.columns.map(([h, links]) => (
          <div key={h} style={{ display: "grid", gap: 9, alignContent: "start", fontSize: 13.5, lineHeight: 1.4 }}>
            <strong style={{ color: "var(--footer-heading)", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14 }}>{h}</strong>
            {links.map(([l, href]) => <a key={l} href={href.startsWith("#") ? anchorHref(href) : pathHref(href)} style={{ color: "var(--footer-link)", textDecoration: "none", whiteSpace: "nowrap" }}>{l}</a>)}
          </div>
        ))}
      </div>
      <div style={{ maxWidth: 1180, margin: "36px auto 0", paddingTop: 22, borderTop: "1px solid var(--footer-border)", display: "grid", gap: 10 }}>
        <p style={{ ...footerStyle, margin: 0 }}>{company.footer_copyright}</p>
        <p style={{ ...footerStyle, margin: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
          <a href={pathHref("/contacts")} style={linkStyle}>{F.contact}</a>
          <span style={{ color: "var(--footer-faint)" }}>·</span>
          <a href={pathHref(company.privacy_path || "/privacy")} style={linkStyle}>{F.privacy}</a>
          <span style={{ color: "var(--footer-faint)" }}>·</span>
          <a href={pathHref(company.personal_data_path || "/personal-data")} style={linkStyle}>{F.personalData}</a>
          <span style={{ color: "var(--footer-faint)" }}>·</span>
          <a href={pathHref(company.cookies_path || "/cookies")} style={linkStyle}>{F.cookies}</a>
          <span style={{ color: "var(--footer-faint)" }}>·</span>
          <a href={pathHref(company.terms_path || "/terms")} style={linkStyle}>{F.terms}</a>
        </p>
        <p style={{ ...footerStyle, margin: 0 }}>{F.closing}</p>
      </div>
    </footer>
  );
}
